import { useEffect, useRef, useCallback } from "react";
import api from "../../api/api";
import { toast } from "react-toastify";
import useWebSocket from "../../hooks/useWebSocket";
import { useTranslation } from "../../hooks/useTranslation";
import globalTranslationStore from "../../utils/globalTranslationStore";
import { useSkinModals } from "../../skins/SkinModalContext";
import { useCheckout } from "../../context/CheckoutContext";

const PaymentProcessingModal = ({
  show,
  setShow,
  heading,
  content,
  transactionId,
  setTransactionStatus,
  setTransactionStatusModal,
}) => {
  // If the active skin registered a modal provider, defer UI to it.
  const skinModals = useSkinModals();
  const { brandColor } = useCheckout();
  const accent = brandColor || "#0f172a";
  const { t } = useTranslation();
  const attemptsRef = useRef(0);

    // Use translations for default values (React hooks work fine for UI components)
  const modalHeading = heading || t('modals.paymentProcessing.title');
  const modalContent = content || t('modals.paymentProcessing.message');

  useWebSocket(
    // Only open the socket while the processing UI is shown — otherwise a skin
    // remount (or catalog setting transactionId early) opens/closes /ws needlessly.
    show && transactionId ? transactionId : null,
    (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.status) {

          if (data.status === "pending") {
            attemptsRef.current += 1;

            if (attemptsRef.current >= 10) {
              setShow(false);
              setTransactionStatusModal(true);
              setTransactionStatus("pending");
              const message = globalTranslationStore.isReady() 
                ? globalTranslationStore.translate('messages.info.transactionPending')
                : "Transaction is still pending. Please contact your Merchant.";
              toast.error(message);
              return;
            }

            const pendingMessage = globalTranslationStore.isReady()
              ? globalTranslationStore.translate('messages.info.pendingStatus')
              : "Transaction is still pending...";
            toast.info(pendingMessage);
            return;
          }

          // Reset attempts if not pending
          attemptsRef.current = 0;

          setTransactionStatus && setTransactionStatus(data.status);
          setTransactionStatusModal && setTransactionStatusModal(true);
          setShow(false);

          if (data.status === "success") {
            const successMessage = globalTranslationStore.isReady()
              ? globalTranslationStore.translate('messages.success.transactionSuccess')
              : "Transaction success.";
            toast.success(successMessage);
          } else if (data.status === "failed") {
            const failedMessage = globalTranslationStore.isReady()
              ? globalTranslationStore.translate('messages.error.paymentFailed')
              : "Payment failed. Please try again.";
            toast.error(failedMessage);
          } else {
            const pendingMessage = globalTranslationStore.isReady()
              ? globalTranslationStore.translate('messages.info.transactionPending')
              : "Transaction is still pending. Please contact your Merchant.";
            toast.info(pendingMessage);
          }
        }
      } catch (e) {
        console.error("WebSocket message parsing failed", e);
      }
    },
    undefined,
    undefined,
    (error) => {
      console.error("WebSocket error", error);
      const errorMessage = globalTranslationStore.isReady()
        ? globalTranslationStore.translate('messages.error.websocketError')
        : "WebSocket connection error.";
      toast.error(errorMessage);
    }
  );

  useEffect(() => {
    return () => {
      attemptsRef.current = 0;
    };
  }, []);

  // Push state to skin (if any) and render nothing — the skin owns the UI.
  // Hooks above (incl. useWebSocket) still run, so the payment status flow
  // is unchanged. Only the stable callbacks are placed in deps; including the
  // whole `skinModals` object would trigger a re-run every time context state
  // changes (since useMemo recreates the value), causing an infinite loop.
  const openProcessing  = skinModals?.openProcessing;
  const closeProcessing = skinModals?.closeProcessing;
  useEffect(() => {
    if (!openProcessing || !closeProcessing) return;
    if (show) {
      openProcessing({
        heading: modalHeading,
        content: modalContent,
        transactionId,
      });
    } else {
      closeProcessing();
    }
  }, [openProcessing, closeProcessing, show, modalHeading, modalContent, transactionId]);

  if (skinModals) return null;

  if (!show) return null;

  return (
    <div className="altruon-pp-overlay" role="status" aria-live="polite">
      <div className="altruon-pp-card">
        <div
          className="altruon-pp-spinner"
          style={{ borderTopColor: accent }}
        />
        <h2 className="altruon-pp-title">{modalHeading}</h2>
        <p className="altruon-pp-msg">{modalContent}</p>
        {transactionId && (
          <p className="altruon-pp-txn">
            {t("modals.paymentProcessing.transactionId", {
              variables: { id: transactionId },
            })}
          </p>
        )}
      </div>

      <style jsx>{`
        .altruon-pp-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: ppFade 0.2s ease;
        }
        .altruon-pp-card {
          width: 100%;
          max-width: 360px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          box-shadow: 0 20px 50px -10px rgba(15, 23, 42, 0.25);
          padding: 32px 28px;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter",
            "Segoe UI", Helvetica, Arial, sans-serif;
          animation: ppSlide 0.3s ease;
        }
        .altruon-pp-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e5e7eb;
          border-top-color: #0f172a;
          border-radius: 50%;
          margin: 0 auto 22px;
          animation: ppSpin 0.75s linear infinite;
        }
        .altruon-pp-title {
          font-size: 17px;
          font-weight: 600;
          color: #0f172a;
          letter-spacing: -0.01em;
          margin: 0 0 8px;
        }
        .altruon-pp-msg {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
          margin: 0;
        }
        .altruon-pp-txn {
          margin-top: 18px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12px;
          color: #94a3b8;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 6px 10px;
          border-radius: 8px;
          display: inline-block;
          word-break: break-all;
        }
        @keyframes ppFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes ppSlide {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes ppSpin { to { transform: rotate(360deg) } }

        @media (max-width: 480px) {
          .altruon-pp-card { padding: 24px 20px; }
          .altruon-pp-spinner { width: 36px; height: 36px; }
          .altruon-pp-title { font-size: 16px; }
        }
      `}</style>
    </div>
  );
};

export default PaymentProcessingModal;
